import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Modal, Badge, Nav } from 'react-bootstrap';
import { Plus, ChatDots, Eye, Code } from 'react-bootstrap-icons';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const MeetingNotes = ({ activeProfileId }) => {
    const [meetingNotes, setMeetingNotes] = useState([]);
    const [showMeetingNoteModal, setShowMeetingNoteModal] = useState(false);
    const [selectedMeetingNote, setSelectedMeetingNote] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);

    useEffect(() => {
        if (activeProfileId) {
            fetchMeetingNotes();
        }
    }, [activeProfileId]);

    const fetchMeetingNotes = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/meeting-notes`);
            setMeetingNotes(response.data);
        } catch (error) {
            console.error('Error fetching meeting notes:', error);
        }
    };

    const handleEditMeetingNote = (note) => {
        setSelectedMeetingNote(note);
        setShowMeetingNoteModal(true);
    };

    const handleSubmitMeetingNote = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        try {
            if (selectedMeetingNote) {
                await axios.put(`${API_BASE_URL}/api/life/meeting-notes/${selectedMeetingNote.id}`, formData);
            } else {
                await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/meeting-notes`, formData);
            }
            fetchMeetingNotes();
            setShowMeetingNoteModal(false);
            setSelectedMeetingNote(null);
            e.target.reset();
        } catch (error) {
            console.error('Error saving meeting note:', error);
        }
    };

    return (
        <div className="meeting-notes-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4><ChatDots className="me-2" />Meeting Notes</h4>
                <Button variant="primary" onClick={() => setShowMeetingNoteModal(true)}>
                    <Plus /> Add Meeting Note
                </Button>
            </div>
            
            {meetingNotes.length === 0 ? (
                <Card className="text-center p-4">
                    <p className="text-muted">No meeting notes yet. Start logging your meetings, discussions, and 1:1s!</p>
                </Card>
            ) : (
                <div className="meeting-notes-list">
                    {meetingNotes.map(note => (
                        <Card key={note.id} className="mb-3">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h6>{note.title}</h6>
                                        <p className="text-muted mb-2">
                                            {new Date(note.meeting_date).toLocaleDateString()} • {note.meeting_type}
                                        </p>
                                        {note.attendees && <p><strong>Attendees:</strong> {note.attendees}</p>}
                                        {note.agenda && <p><strong>Agenda:</strong> {note.agenda}</p>}
                                        {note.minutes && <p><strong>Minutes:</strong> {note.minutes}</p>}
                                        {note.action_items && <p><strong>Action Items:</strong> {note.action_items}</p>}
                                        {note.decisions && <p><strong>Decisions/Follow-up:</strong> {note.decisions}</p>}
                                        {note.notes && <p><strong>Additional Notes:</strong> {note.notes}</p>}
                                    </div>
                                    <Button variant="outline-primary" size="sm" onClick={() => handleEditMeetingNote(note)}>
                                        Edit
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            )}

            {/* Add/Edit Meeting Note Modal */}
            <Modal show={showMeetingNoteModal} onHide={() => {setShowMeetingNoteModal(false); setSelectedMeetingNote(null);}} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedMeetingNote ? 'Edit Meeting Note' : 'Add Meeting Note'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmitMeetingNote}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Meeting Date *</Form.Label>
                                    <Form.Control 
                                        name="meeting_date" 
                                        type="date" 
                                        defaultValue={selectedMeetingNote ? selectedMeetingNote.meeting_date.split('T')[0] : ''} 
                                        required 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Meeting Type</Form.Label>
                                    <Form.Select name="meeting_type" defaultValue={selectedMeetingNote?.meeting_type || 'Team Meeting'}>
                                        <option value="Team Meeting">Team Meeting</option>
                                        <option value="1:1 Meeting">1:1 Meeting</option>
                                        <option value="Discussion">Discussion</option>
                                        <option value="Standup">Standup</option>
                                        <option value="Review Meeting">Review Meeting</option>
                                        <option value="Planning Meeting">Planning Meeting</option>
                                        <option value="Client Meeting">Client Meeting</option>
                                        <option value="Other">Other</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Title *</Form.Label>
                            <Form.Control 
                                name="title" 
                                defaultValue={selectedMeetingNote?.title || ''} 
                                required 
                                placeholder="e.g., Weekly Team Sync, Q1 Planning" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Attendees</Form.Label>
                            <Form.Control 
                                name="attendees" 
                                defaultValue={selectedMeetingNote?.attendees || ''} 
                                placeholder="List of attendees" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Agenda</Form.Label>
                            <Form.Control 
                                name="agenda" 
                                as="textarea" 
                                rows={3} 
                                defaultValue={selectedMeetingNote?.agenda || ''} 
                                placeholder="Meeting agenda and topics to discuss" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Minutes of Meeting (MOM)</Form.Label>
                            <Form.Control 
                                name="minutes" 
                                as="textarea" 
                                rows={4} 
                                defaultValue={selectedMeetingNote?.minutes || ''} 
                                placeholder="Key discussion points and decisions made" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Action Items (AI)</Form.Label>
                            <Form.Control 
                                name="action_items" 
                                as="textarea" 
                                rows={3} 
                                defaultValue={selectedMeetingNote?.action_items || ''} 
                                placeholder="Action items with owners and ETAs" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Decisions/Follow-up Actions/Deadlines (DFAD)</Form.Label>
                            <Form.Control 
                                name="decisions" 
                                as="textarea" 
                                rows={3} 
                                defaultValue={selectedMeetingNote?.decisions || ''} 
                                placeholder="Key decisions, follow-up actions, and deadlines" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Additional Notes</Form.Label>
                            <Form.Control 
                                name="notes" 
                                as="textarea" 
                                rows={2} 
                                defaultValue={selectedMeetingNote?.notes || ''} 
                                placeholder="Any additional notes or observations" 
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => {setShowMeetingNoteModal(false); setSelectedMeetingNote(null);}}>Cancel</Button>
                            <Button variant="primary" type="submit">{selectedMeetingNote ? 'Update Meeting Note' : 'Add Meeting Note'}</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default MeetingNotes;
